import axios from 'axios';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL;
if (!API_BASE_URL) {
  throw new Error('NEXT_PUBLIC_API_URL environment variable is required');
}

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Interceptor para adicionar token JWT automaticamente
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('survey_token');

    // Log para depuração de problemas de autenticação (APENAS EM DEV OU ERRO 401)
    if (process.env.NODE_ENV === 'development' || !token) {
      console.log('🔍 [API Request] Auth Token:', token ? 'Present' : 'Missing', 'URL:', config.url);
    }

    if (token) {
      console.log('🔍 [API] Adding Auth Header (x-survey-token). Token length:', token.length, 'Last 4:', token.slice(-4));
      // Usar header customizado para evitar interferência do Google Cloud Run/IAP
      config.headers['x-survey-token'] = token;
      // Manter Authorization vazio ou remover para evitar conflito
      delete config.headers.Authorization;
    } else {
      console.warn('⚠️ [API] No token found in localStorage (survey_token)');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para lidar com erros de resposta (401)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Se receber 401, o token é inválido ou expirou.
      // Limpar localStorage para forçar novo login na próxima verificação
      if (typeof window !== 'undefined') {
        // Não limpar imediatamente se for endpoint de login ('/auth')
        const isAuthRequest = error.config?.url?.includes('/auth');
        if (!isAuthRequest) {
          console.warn('⚠️ Sessão expirada ou inválida (401). Limpando dados locais.');
          localStorage.removeItem('survey_token');
          // Não limpar surveyUser/surveyToken (secret) imediatamente para permitir 
          // que a UI mostre mensagem adequada ou tente reautenticar se implementar refresh
          // Mas como não temos refresh token, talvez seja melhor limpar tudo?
          // Vamos limpar o token JWT para garantir que useAuth detecte como deslogado
        }
      }
    }
    return Promise.reject(error);
  }
);

export interface PesquisaAuthRequest {
  uuid?: string;
  cpf?: string;
  secret: string; // token de acesso
}

export interface PesquisaAuthResponse {
  access_token: string;
  pesquisa_colaborador: {
    id: number;
    uuid: string;
    colaborador_id?: number;
    colaborador: {
      nome: string;
      email?: string;
      cargo?: { nome: string };
      setor?: { nome: string };
    };
    pesquisa_empresa: {
      pesquisa: {
        id: number;
        pesquisa: string;
        versao?: string;
        escala?: string;
        tipo?: string;
        pesquisa_questao: Array<{
          id: number;
          questao: string;
          ordem: number;
          escala_resposta_id?: string | null;
          pesquisa_esfera_id?: number | null;
          enabled: boolean;
        }>;
      };
    };
  };
}

export interface PesquisaStatus {
  uuid: string;
  enabled: boolean;
  is_finished: boolean;
  pesquisa_empresa: {
    enabled: boolean;
    is_vigente: boolean;
    is_finished: boolean;
  };
}

export interface PesquisaContext {
  id: number;
  uuid: string;
  colaborador: {
    nome: string;
    email?: string;
    telefone?: string;
    setor?: { nome: string };
    cargo?: { nome: string };
  };
  pesquisa_empresa: {
    pesquisa: {
      id: number;
      pesquisa: string;
      versao?: string;
      escala?: string;
      tipo?: string;
      pesquisa_questao: Array<{
        id: number;
        questao: string;
        ordem: number;
        escala_resposta_id?: string | null;
        pesquisa_esfera_id?: number | null;
        enabled: boolean;
      }>;
    };
  };
  respostas?: Record<string, any>; // Respostas salvas do banco de dados
  dados_preenchidos?: boolean; // Flag indicando se dados obrigatórios foram preenchidos
  lideranca?: boolean;
  tempo_contratacao?: '0-1' | '2-5' | 'MAIS_5';
}

export interface SubmitResponseRequest {
  questao_id: number;
  resposta: string | number;
}

export interface SubmitResponseResponse {
  percentual: number;
  totalRespostas: number;
  totalQuestoes: number;
}

export interface FinalizarPesquisaRequest {
  idade?: number;
  genero?: string;
  estado_civil?: string;
  escolaridade?: string;
  tempo_empresa?: string;
  jornada_trabalho?: string;
  tipo_contrato?: string;
  faixa_salarial?: string;
  observacoes?: string;
}

export interface BehaviorTrackingEvent {
  event_type: 'hover' | 'time_on_question' | 'answer_change' | 'navigation' | 'text_selection';
  question_id?: number;
  event_data: Record<string, any>;
}

export interface BatchBehaviorTrackingRequest {
  events: BehaviorTrackingEvent[];
}

export interface UpdateColaboradorDadosDto {
  email?: string;
  telefone?: string;
  sexo?: 'M' | 'F';
  escolaridade_id?: number;
  etnia?: number;
  orientacao_sexual?: number;
  modalidade_trabalho?: 'PRESENCIAL' | 'HIBRIDO' | 'HOME_OFFICE';
  modalidade_contrato?: 'CLT' | 'PJ';
  pcd?: boolean;
  lideranca?: boolean;
  tempo_contratacao?: '0-1' | '2-5' | 'MAIS_5';
}

export interface AcademicFormation {
  id: number;
  valor: string;
  descricao: string;
}

export interface Etnia {
  id: number;
  valor: string;
  descricao: string;
}

export interface OrientacaoSexual {
  id: number;
  codigo: string;
  opcao: string;
  descricao: string;
}

export class PesquisasApiService {
  // Autenticar na pesquisa
  static async authenticate(data: PesquisaAuthRequest): Promise<PesquisaAuthResponse> {
    const response = await api.post('/public/pesquisas/auth', data);
    return response.data;
  }

  // Verificar status da pesquisa pelo UUID (público, sem autenticação)
  static async checkStatus(uuid: string): Promise<PesquisaStatus> {
    const response = await api.get(`/public/pesquisas/${uuid}/status`);
    return response.data;
  }

  // Obter contexto da pesquisa (questões, dados do colaborador)
  static async getContext(uuid: string): Promise<PesquisaContext> {
    const response = await api.get(`/public/pesquisas/${uuid}/context`);
    return response.data;
  }

  // Salvar resposta individual
  static async submitResponse(
    uuid: string,
    data: SubmitResponseRequest
  ): Promise<SubmitResponseResponse> {
    const response = await api.post(`/public/pesquisas/${uuid}/resposta`, data);
    return response.data;
  }

  // Finalizar pesquisa
  static async finalizarPesquisa(
    uuid: string,
    data: FinalizarPesquisaRequest
  ): Promise<{ success: boolean }> {
    const response = await api.post(`/public/pesquisas/${uuid}/finalizar`, data);
    return response.data;
  }

  // Salvar token JWT no localStorage
  static setToken(token: string) {
    localStorage.setItem('survey_token', token);
  }

  // Obter token JWT do localStorage
  static getToken(): string | null {
    return localStorage.getItem('survey_token');
  }

  // Remover token JWT do localStorage
  static removeToken() {
    localStorage.removeItem('survey_token');
  }

  // Salvar eventos de comportamento
  static async saveBehaviorTracking(
    uuid: string,
    data: BatchBehaviorTrackingRequest
  ): Promise<{ saved: number }> {
    const response = await api.post(`/public/pesquisas/${uuid}/behavior-tracking`, data);
    return response.data;
  }

  // Atualizar dados do colaborador antes de iniciar a pesquisa
  static async updateColaboradorDados(
    uuid: string,
    data: UpdateColaboradorDadosDto
  ): Promise<{ success: boolean }> {
    const response = await api.patch(`/public/pesquisas/${uuid}/dados-colaborador`, data);
    return response.data;
  }

  // Buscar formações acadêmicas disponíveis
  static async getAcademicFormations(): Promise<AcademicFormation[]> {
    const response = await api.get('/public/domain/academic-formation');
    return response.data;
  }

  // Buscar etnias disponíveis
  static async getEtnias(): Promise<Etnia[]> {
    const response = await api.get('/public/domain/etnia');
    return response.data;
  }

  // Buscar orientações sexuais disponíveis
  static async getOrientacoesSexuais(): Promise<OrientacaoSexual[]> {
    const response = await api.get('/public/domain/orientacao-sexual');
    return response.data;
  }
}
